// src/pages/Production/WasteReport.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#06C167',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0',
        marginBottom: '30px'
    },
    summaryCards: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '30px'
    },
    summaryCard: {
        background: '#f8fafc',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #e2e8f0'
    },
    wasteCard: {
        background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
        color: 'white',
        border: 'none'
    },
    cardTitle: {
        fontSize: '13px',
        color: '#64748b',
        marginBottom: '6px'
    },
    cardValue: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#0f172a'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #e2e8f0'
    },
    th: {
        background: '#f8fafc',
        padding: '14px 16px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase'
    },
    td: {
        padding: '14px 16px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    loadingContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    }
};

function WasteReport() {
    const { userData } = useAuth();
    const [productionOrders, setProductionOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        const unsubOrders = onSnapshot(
            query(collection(db, 'companies', companyId, 'productionOrders'), orderBy('createdAt', 'desc')),
            snap => {
                setProductionOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            }
        );

        return () => unsubOrders();
    }, [userData]);

    const wasteData = useMemo(() => {
        const ordersWithWaste = productionOrders.map(order => {
            // Calculate waste from order data
            const plannedQuantity = order.quantity || 0;
            const completedQuantity = order.completedQuantity || 0;
            const wasteQuantity = order.wasteQuantity || 0;

            // If no explicit waste, calculate from stages
            let stageWaste = 0;
            if (order.stages) {
                order.stages.forEach(stage => {
                    stageWaste += stage.wasteQuantity || 0;
                });
            }

            const totalWaste = wasteQuantity || stageWaste;
            const wastePercentage = plannedQuantity > 0 ? (totalWaste / plannedQuantity) * 100 : 0;

            const orderDate = order.startDate?.toDate?.() || order.createdAt?.toDate?.() || new Date();

            return {
                id: order.id,
                orderNo: order.id.substring(0, 8).toUpperCase(),
                productName: order.productName || 'Bilinmeyen Ürün',
                plannedQuantity,
                completedQuantity,
                wasteQuantity: totalWaste,
                wastePercentage,
                date: orderDate,
                status: order.status || 'Beklemede'
            };
        }).filter(o => o.wasteQuantity > 0 || o.completedQuantity > 0);

        const totalPlanned = ordersWithWaste.reduce((sum, o) => sum + o.plannedQuantity, 0);
        const totalCompleted = ordersWithWaste.reduce((sum, o) => sum + o.completedQuantity, 0);
        const totalWaste = ordersWithWaste.reduce((sum, o) => sum + o.wasteQuantity, 0);
        const avgWastePercentage = totalPlanned > 0 ? (totalWaste / totalPlanned) * 100 : 0;

        return {
            orders: ordersWithWaste.sort((a, b) => b.date - a.date),
            totals: {
                orderCount: ordersWithWaste.length,
                totalPlanned,
                totalCompleted,
                totalWaste,
                avgWastePercentage
            }
        };
    }, [productionOrders]);

    const formatDate = (date) => {
        return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                        <p>Fire verileri yükleniyor...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Link to="/uretim/raporlar" style={styles.backLink}>← Üretim Raporları</Link>
            </div>

            <div style={styles.box}>
                <h2 style={styles.title}>Üretim Emri Bazlı Fire Raporu</h2>
                <p style={{ color: '#64748b', marginTop: '8px', marginBottom: '24px' }}>
                    Üretim aşamalarında oluşan fire miktarları
                </p>

                <div style={styles.summaryCards}>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>Toplam Emir</div>
                        <div style={styles.cardValue}>{wasteData.totals.orderCount}</div>
                    </div>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>Planlanan Üretim</div>
                        <div style={styles.cardValue}>{wasteData.totals.totalPlanned} adet</div>
                    </div>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>Tamamlanan</div>
                        <div style={styles.cardValue}>{wasteData.totals.totalCompleted} adet</div>
                    </div>
                    <div style={{
                        ...styles.summaryCard,
                        ...(wasteData.totals.totalWaste > 0 ? styles.wasteCard : {})
                    }}>
                        <div style={{ ...styles.cardTitle, color: wasteData.totals.totalWaste > 0 ? 'rgba(255,255,255,0.8)' : undefined }}>
                            Toplam Fire
                        </div>
                        <div style={{ ...styles.cardValue, color: wasteData.totals.totalWaste > 0 ? 'white' : undefined }}>
                            {wasteData.totals.totalWaste} adet
                        </div>
                    </div>
                    <div style={styles.summaryCard}>
                        <div style={styles.cardTitle}>Ortalama Fire Oranı</div>
                        <div style={{
                            ...styles.cardValue,
                            color: wasteData.totals.avgWastePercentage > 5 ? '#dc2626' : wasteData.totals.avgWastePercentage > 2 ? '#f59e0b' : '#059669'
                        }}>
                            %{wasteData.totals.avgWastePercentage.toFixed(1)}
                        </div>
                    </div>
                </div>

                {wasteData.orders.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔥</div>
                        <h3 style={{ color: '#374151' }}>Fire Kaydı Bulunamadı</h3>
                        <p>Henüz fire kaydı olan üretim emri bulunmuyor.</p>
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Emir No</th>
                                <th style={styles.th}>Ürün</th>
                                <th style={styles.th}>Tarih</th>
                                <th style={styles.th}>Planlanan</th>
                                <th style={styles.th}>Tamamlanan</th>
                                <th style={styles.th}>Fire</th>
                                <th style={styles.th}>Fire Oranı</th>
                                <th style={styles.th}>Durum</th>
                            </tr>
                        </thead>
                        <tbody>
                            {wasteData.orders.map(order => (
                                <tr key={order.id}>
                                    <td style={{ ...styles.td, fontWeight: '600' }}>{order.orderNo}</td>
                                    <td style={styles.td}>{order.productName}</td>
                                    <td style={styles.td}>{formatDate(order.date)}</td>
                                    <td style={styles.td}>{order.plannedQuantity} adet</td>
                                    <td style={styles.td}>{order.completedQuantity} adet</td>
                                    <td style={{
                                        ...styles.td,
                                        fontWeight: '600',
                                        color: order.wasteQuantity > 0 ? '#f59e0b' : '#374151'
                                    }}>
                                        {order.wasteQuantity} adet
                                    </td>
                                    <td style={{
                                        ...styles.td,
                                        color: order.wastePercentage > 5 ? '#dc2626' : order.wastePercentage > 2 ? '#f59e0b' : '#059669'
                                    }}>
                                        %{order.wastePercentage.toFixed(1)}
                                    </td>
                                    <td style={styles.td}>{order.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default WasteReport;
